import React, { useMemo } from 'react';

import { useSellerLabelProviders } from '../../../../hooks/use-seller-label-providers';
import { useShouldShowNewLineupByoaUi } from '../../../../hooks/non-wallet-access-hooks';
import { useNonWalletProviderFeatureGate } from '../../../../feature-gates/use-non-wallet-provider-feature-gate';
import SellerLabelProviderDataContextProvider from '../../providers/providers-list/contexts/seller-label-provider-data-context-provider';
import ServiceVisibilityModalContextProvider from '../../providers/service-visibility-modal/context-provider';
import ProviderSettingsModalContextProvider from '../../providers/provider-settings-modal/context-provider';
import DeleteLabelProviderModalContextProvider from '../../providers/delete-label-provider-modal/context-provider';
import AddCarriersGrid from './add-carriers-grid';
import AdditionalCarriersTable from './carrier-table';
import { Providers, isByolOnlyLabelProvider } from '../../../../enums/providers';

import style from './carriers-additional.module.less';

/**
 * Select only the non-wallet providers that a user is able to act on.
 * @todo - Our new lineup work is distinct from that of all previous plans.
 * - Previously we would show the user's unusable non-wallet carriers in half-opacity in a table.
 * - In the new lineup world we are asked to hide all previously-connected non-wallet carriers and show an empty table.
 * @param {object} args
 * @param {object[]} args.sellerLabelProviders
 * @param {boolean} args.isFeatureEnabled does seller has nonWalletProviders feature
 * @param {boolean} args.shouldHideCarriersWhenFeatureIsDisabled are we showing the user the new lineup ux/ui
 * @returns {string[]}
 */
const getFilteredNonWalletCarriers = ({ sellerLabelProviders, isFeatureEnabled, shouldHideCarriersWhenFeatureIsDisabled }) => {
  if (!isFeatureEnabled && shouldHideCarriersWhenFeatureIsDisabled) {
    return [];
  }

  return (sellerLabelProviders || [])
    .filter(
      (p) =>
        !p.walletId && p.labelProviderId !== Providers.ClientSharedCarriers.labelProviderId && !isByolOnlyLabelProvider(p.labelProviderId),
    )
    .map((p) => p.sellerLabelProviderId);
};

export const AdditionalCarriersContent = () => {
  const sellerLabelProviders = useSellerLabelProviders();
  const { isFeatureEnabled } = useNonWalletProviderFeatureGate();
  const shouldShowNewLineupByoaUi = useShouldShowNewLineupByoaUi();

  const sellerLabelProviderIds = useMemo(
    () =>
      getFilteredNonWalletCarriers({
        sellerLabelProviders,
        isFeatureEnabled,
        shouldHideCarriersWhenFeatureIsDisabled: shouldShowNewLineupByoaUi,
      }),
    [isFeatureEnabled, sellerLabelProviders, shouldShowNewLineupByoaUi],
  );

  if (!shouldShowNewLineupByoaUi && !sellerLabelProviderIds.length) {
    return (
      <div className={style['additional-carriers-wrapper']}>
        <AddCarriersGrid />
      </div>
    );
  }

  return (
    <SellerLabelProviderDataContextProvider>
      <ServiceVisibilityModalContextProvider>
        <ProviderSettingsModalContextProvider>
          <DeleteLabelProviderModalContextProvider>
            <AdditionalCarriersTable sellerLabelProviderIds={sellerLabelProviderIds} />
          </DeleteLabelProviderModalContextProvider>
        </ProviderSettingsModalContextProvider>
      </ServiceVisibilityModalContextProvider>
    </SellerLabelProviderDataContextProvider>
  );
};
